// Context provider that wires an axios client + plans API into every hook
// downstream. Apps mount this once near their auth boundary:
//
//   <PlanProvider apiClient={createPlanApiClient(...)}>
//     <App />
//   </PlanProvider>
//
// Hooks read from this context — they never reach for global state — so
// SSR / Storybook / tests can isolate easily.

import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';
import type { AxiosInstance } from 'axios';
import { plansApi, PlansApi } from '../api/plans';
import type { PlanAlert } from '../types';

interface PlanContextShape {
  api: PlansApi;
  client: AxiosInstance;
  /** Login response includes a planAlert; the app reports it here. */
  planAlert: PlanAlert | null;
  reportPlanAlert: (alert: PlanAlert | null) => void;
  upgradeUrl: string;
}

const PlanContext = createContext<PlanContextShape | null>(null);

export interface PlanProviderProps {
  apiClient: AxiosInstance;
  upgradeUrl?: string;
  children: ReactNode;
}

export function PlanProvider({
  apiClient,
  upgradeUrl = '/billing/plans',
  children,
}: PlanProviderProps) {
  const [planAlert, setPlanAlert] = useState<PlanAlert | null>(null);

  const reportPlanAlert = useCallback((a: PlanAlert | null) => {
    setPlanAlert(a);
  }, []);

  const value = useMemo<PlanContextShape>(
    () => ({
      api: plansApi(apiClient),
      client: apiClient,
      planAlert,
      reportPlanAlert,
      upgradeUrl,
    }),
    [apiClient, planAlert, reportPlanAlert, upgradeUrl],
  );

  return (
    <PlanContext.Provider value={value}>{children}</PlanContext.Provider>
  );
}

export function usePlanContext(): PlanContextShape {
  const ctx = useContext(PlanContext);
  if (!ctx) {
    throw new Error(
      '@onxite/plan-sdk: missing <PlanProvider>. Wrap your app with <PlanProvider apiClient={...}>.',
    );
  }
  return ctx;
}
